import api from '@/lib/api';

import type {
  CategoryRankingEntry,
  CategoryRankingEntryDetail,
  CategoryRankingPointsRule,
  ClubCategoryRankingParams,
  PlayerCategoryRankingEntriesParams,
  RankingImportBatch,
  RankingImportForm,
  RankingImportResult,
} from './types';

export const getClubCategoryRanking = async (
  clubId: string,
  params: ClubCategoryRankingParams,
): Promise<{ entries: CategoryRankingEntry[]; total: number }> => {
  const response = await api.get(`/clubs/${clubId}/category-ranking`, {
    params: {
      categoryId: params.categoryId,
      gender: params.gender,
      page: params.page ?? 0,
      period: params.period,
      query: params.query,
      size: params.size ?? 25,
      sport: params.sport,
    },
  });

  return {
    entries: response.data.content ?? [],
    total: response.data.page.totalElements,
  };
};

export const getPlayerCategoryRankingEntries = async (
  clubId: string,
  params: PlayerCategoryRankingEntriesParams,
): Promise<{ entries: CategoryRankingEntryDetail[]; total: number }> => {
  const response = await api.get(`/clubs/${clubId}/category-ranking/entries`, {
    params: {
      categoryId: params.categoryId,
      email: params.email,
      page: params.page ?? 0,
      period: params.period,
      size: params.size ?? 5,
      sport: params.sport,
    },
  });

  return {
    entries: response.data.content ?? [],
    total: response.data.page.totalElements,
  };
};

export const getCategoryRankingPointsTable = async (): Promise<
  CategoryRankingPointsRule[]
> => {
  const response = await api.get('/leaderboard/category-ranking/points-table');
  return response.data;
};

/**
 * Imports a batch of ranking points into the club. With `dryRun` the backend validates and
 * resolves every row without persisting, which feeds the wizard's confirmation step.
 *
 * Always resolves with 200 when the batch itself is well formed: per-row errors travel inside
 * `result.rows` with `batchId` null, not as an HTTP error.
 */
export const importClubRankingPoints = async (
  clubId: string,
  form: RankingImportForm,
  dryRun: boolean,
): Promise<RankingImportResult> => {
  const response = await api.post(
    `/clubs/${clubId}/category-ranking/imports`,
    form,
    { params: { dryRun } },
  );
  return response.data;
};

export const getClubRankingImports = async (
  clubId: string,
): Promise<RankingImportBatch[]> => {
  const response = await api.get(`/clubs/${clubId}/category-ranking/imports`);
  return response.data ?? [];
};

export const deleteClubRankingImport = async (
  clubId: string,
  batchId: string,
): Promise<void> => {
  await api.delete(`/clubs/${clubId}/category-ranking/imports/${batchId}`);
};
